import type { ReactNode, ReactElement } from 'react';
import { SignOut, MagnifyingGlass } from '@phosphor-icons/react';
import { useFeatureFlag, useKillswitch } from '../lib/feature-flags.js';
import { Link, usePathname } from '../lib/router.js';
import { unregisterNativeToken } from '../lib/native.js';
import { FichajeChip } from '../modules/fichaje/FichajeChip.js';
import { OnlineStatusChip } from './OnlineStatusChip.js';
import { ImpersonationBanner } from './ImpersonationBanner.js';
import { UserPreferencesMenu } from './UserPreferencesMenu.js';
import { NotificationsBell } from './NotificationsBell.js';
import { MobileTabBar } from './MobileTabBar.js';
import { activeHrefOf, useVisibleNav, type NavItem } from './nav-shared.js';

interface Props {
  children: ReactNode;
  title?: string;
  subtitle?: string;
  actions?: ReactNode;
}

export function AppShell({ children, title, subtitle, actions }: Props): ReactElement {
  // R0 responsive · la matriz de visibilidad vive en nav-shared (compartida con MobileTabBar)
  const nav = useVisibleNav();
  return (
    <div className="min-h-screen bg-[var(--color-munya-cream)] text-[var(--color-munya-coffee)]">
      <ImpersonationBanner />
      <GlobalFlagBanners />
      {/* R0 · mn-safe-top reserva el notch de iOS (Capacitor) · en móvil la cabecera crece a 48px
          para dedos y la navegación central desaparece (vive en el MobileTabBar inferior) */}
      <header className="mn-header border-b border-black/10 backdrop-blur sticky top-0 z-30 mn-safe-top mn-safe-x">
        {/* · #429 · misma trampa que en <main>: `lg:px-12` valía 96px por lado, no 48, y a
            1.280 la barra no daba de sí — el logotipo se comía «Inicio» y «Administración» se
            montaba sobre el reloj de la jornada. En rem explícito, alineado con el contenido. */}
        <div className="w-full px-4 md:px-[2rem] lg:px-[3.5rem] h-[3rem] lg:h-8 grid grid-cols-[auto_1fr_auto] items-center gap-2 md:gap-4">
          {/* IZQ · logo */}
          <Link
            href="/"
            className="flex items-center shrink-0 justify-self-start"
            aria-label="MūnyaOS · Inicio"
          >
            {/* #151 · logo 1212×222 (ratio 5.46) · ancho explícito + object-contain +
               shrink-0 para que ni el flex ni un reset global de img lo deformen */}
            <img
              src="/logo-munya-os.png"
              alt="MūnyaOS"
              width={109}
              height={20}
              style={{ height: 20, width: 109, maxWidth: 'none' }}
              className="select-none shrink-0 object-contain mn-header-logo"
            />
          </Link>

          {/* CENTRO · navegación (solo desktop · en móvil vive en el tab bar inferior) */}
          {/* · #429 · `min-w-0` + `w-full`: sin ellos el nav se dimensiona por su contenido
              (716px) y se sale de su casilla del grid (597) POR LOS DOS LADOS, tapando el
              logotipo a la izquierda y el reloj de jornada a la derecha. El overflow-x-auto ya
              estaba, pero no llegaba a actuar porque el elemento nunca era más estrecho que su
              contenido. Con esto, cuando no caben todas las secciones, se desplazan de lado. */}
          <nav
            data-tour-target="topbar-nav"
            className="hidden lg:flex items-center gap-0.5 overflow-x-auto min-w-0 w-full justify-self-center"
          >
            <ActiveNav items={nav} />
          </nav>

          {/* DCH · fichaje + notificaciones + search icono + salir
              En móvil: solo fichaje + campana + preferencias (Buscar y Salir van en la hoja "Más") */}
          <div className="flex items-center gap-1.5 lg:gap-1 shrink-0 justify-self-end col-start-3">
            <span data-focus-hide>
              <OnlineStatusChip />
            </span>
            <span data-tour-target="fichaje" data-focus-hide>
              <FichajeChip />
            </span>
            {/* #150 · campanita: aprobaciones pendientes + resoluciones de sugerencias */}
            <NotificationsBell />
            <button
              data-tour-target="search"
              onClick={() => {
                const ev = new KeyboardEvent('keydown', { key: 'k', metaKey: true });
                window.dispatchEvent(ev);
              }}
              className="mn-nav-inactive hidden lg:inline-flex items-center gap-1 px-1.5 h-5 rounded text-xs leading-none transition cursor-pointer"
              title="Buscar"
              aria-label="Buscar"
            >
              <MagnifyingGlass size={13} />
              <kbd className="mn-header-chip text-[9px] px-1 rounded leading-none">⌘K</kbd>
            </button>
            <UserPreferencesMenu />
            <a
              href="/auth/logout"
              onClick={() => {
                unregisterNativeToken();
              }}
              className="mn-nav-inactive hidden lg:inline-flex items-center gap-1 text-xs leading-none transition px-1.5 h-5 rounded"
              title="Cerrar sesión"
            >
              <SignOut size={12} weight="regular" />
              <span className="hidden md:inline">Salir</span>
            </a>
          </div>
        </div>
      </header>

      {/* R0 · pb-28 en móvil deja hueco para el tab bar inferior (56px + safe-area)
          · #429 · los márgenes laterales eran de 112px por lado (`lg:px-14` es una clase de
          escala y --spacing vale 0.5rem: el doble de lo que dice el nombre), 224px de los
          1.280 de pantalla gastados en aire. En rem explícito: 56px por lado, que es la
          medida que se quiso. Igual con md:px-10 → 40px. */}
      <main className="w-full px-4 md:px-[2.5rem] lg:px-[3.5rem] pt-4 md:pt-5 pb-[6.5rem] lg:pb-16">
        {title && (
          <div className="mb-4 flex items-start justify-between gap-6 flex-wrap">
            <div className="min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{title}</h1>
              {subtitle && (
                <p className="mt-1.5 opacity-70 max-w-3xl text-sm md:text-base">{subtitle}</p>
              )}
            </div>
            {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
          </div>
        )}
        {children}
      </main>

      {/* R0 · navegación inferior app-like en móvil/tablet */}
      <MobileTabBar />
    </div>
  );
}

// Marca activo solo el href "más específico" (lógica compartida en nav-shared).
function ActiveNav({ items }: { items: NavItem[] }): ReactElement {
  const pathname = usePathname();
  const activeHref = activeHrefOf(items, pathname);
  return (
    <>
      {items.map((item) => (
        <NavLink key={item.href} {...item} active={item.href === activeHref} />
      ))}
    </>
  );
}

function NavLink({ href, label, icon: Icon, active }: NavItem & { active: boolean }): ReactElement {
  // Etiqueta de tour: deriva del slug del href ('/proyectos' → 'nav-proyectos')
  const tourSlug =
    href === '/' ? 'nav-inicio' : `nav-${href.replace(/^\//, '').split('/')[0] ?? 'unknown'}`;
  return (
    <Link
      href={href}
      data-tour-target={tourSlug}
      className={`inline-flex items-center gap-0.5 px-1 h-5 rounded text-[12px] leading-none whitespace-nowrap transition ${
        active ? 'mn-nav-active font-medium' : 'mn-nav-inactive'
      }`}
    >
      <Icon size={12} weight={active ? 'bold' : 'regular'} />
      <span>{label}</span>
    </Link>
  );
}

// Banners globales según feature flags · visibles en TODA la app
function GlobalFlagBanners(): ReactElement | null {
  const dualMode = useFeatureFlag('migration_odoo_dual_mode');
  // #207 · el aviso de sandbox debe verse SIEMPRE que NO se emita a producción real (para todos
  // los roles, también Dirección). Producción real solo si real_submission ON y sandbox_endpoint
  // OFF; en cualquier otro caso estamos en sandbox (stub o Beel sandbox).
  const realSubmission = useFeatureFlag('verifactu_real_submission');
  const sandboxEndpoint = useFeatureFlag('verifactu_sandbox_endpoint');
  const inSandbox = !realSubmission || sandboxEndpoint;
  const aiKilled = useKillswitch('ai');
  const banners: { text: string; tone: 'warning' | 'info' | 'danger' }[] = [];
  if (dualMode)
    banners.push({
      text: 'Operación dual Odoo · cambios se replican en ambos sistemas',
      tone: 'info',
    });
  if (inSandbox)
    banners.push({
      text: realSubmission
        ? 'Modo sandbox · facturas a Beel sandbox, NO a la AEAT real (verifactu_sandbox_endpoint ON)'
        : 'Modo sandbox · facturas NO se envían a la AEAT (verifactu_real_submission OFF)',
      tone: 'warning',
    });
  if (aiKilled)
    banners.push({
      text: 'Killswitch IA activo · todas las invocaciones a Claude bloqueadas',
      tone: 'danger',
    });
  if (banners.length === 0) return null;
  return (
    <div className="space-y-px">
      {banners.map((b, i) => (
        <div
          key={i}
          className={`px-4 py-1.5 text-[11px] text-center ${
            b.tone === 'danger'
              ? 'bg-[var(--color-munya-destructive)] text-white'
              : b.tone === 'warning'
                ? 'bg-[var(--mn-state-delivery-bg)] text-[var(--mn-state-delivery-fg)]'
                : 'bg-[var(--mn-state-event-bg)] text-[var(--mn-state-event-fg)]'
          }`}
        >
          {b.text}
        </div>
      ))}
    </div>
  );
}
